
import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';

import { FcAutoWriteoffAdjTypeDataDTO, FcSettingsListItemDto, FormGroupValidationRules, SystemGuid } from 'webcommon/shared';
import { FcItemModalContext, FcItemModalMode } from '../../../models';
import { FcItemListBaseComponent, FcListValidationService, FormControlExtended } from '../../generic';

@Component({
  providers: [FcListValidationService],
  selector: 'fc-settings-list',
  templateUrl: './fc-settings-list.component.html',
  styleUrls: ['./fc-settings-list.component.less'],
})
export class FcSettingsListComponent extends FcItemListBaseComponent implements OnInit, OnChanges {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: FcSettingsListItemDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;

  public isEligibilityPayerFindControlRequired = false;

  public accountTypePks: string[] = [];
  public feeSchedulePks: string[] = [];
  public allowedFeeSchedulePks: string[] = [];
  public reimbursementRulePks: string[] = [];
  public eraRuleGroupPks: string[] = [];
  public autoWriteoffCreditTypePks: string[] = [];
  public eligibilityClearingHousePks: string[] = [];
  public eligibilityPayerPks: string[] = [];
  public eligibilityFormatPks: string[] = [];
  public demographicsClearingHousePks: string[] = [];
  public enrollmentClearingHousePks: string[] = [];
  public alternateInsurancePlanPks: string[] = [];
  public englishFormattingTemplatePks: string[] = [];
  public spanishFormattingTemplatePks: string[] = [];

  public accountTypeUidControl = new FormControl<string[]>([]);
  public useProviderFeeScheduleControl = new FormControl<boolean>(true);
  public feeScheduleUidControl = new FormControlExtended<string[]>([]);
  public amountAllowedFeeScheduleUidControl = new FormControl<string[]>([]);
  public eraPayerIdControl = new FormControl<string>('');
  public eraRuleGroupUidControl = new FormControl<string[]>([]);
  public autoWriteoffCreditTypeUidControl = new FormControlExtended<string[]>([]);
  public reimbursementRuleUidControl = new FormControl<string[]>([]);
  public noNetworkControl = new FormControl<boolean>(false);
  public eligibilityClearingHouseUidControl = new FormControl<string[]>([]);
  public eligibilityPayerUidControl = new FormControlExtended<string[]>([]);
  public eligibilityFormatUidControl = new FormControl<string[]>([]);
  public enrollmentClearingHouseUidControl = new FormControl<string[]>([]);
  public demographicsClearingHouseUidControl = new FormControl<string[]>([]);
  public alternateInsurancePlanUidControl = new FormControl<string[]>([]);
  public icdControl = new FormControl<Date>(new Date());
  public emControl = new FormControl<Date>(new Date());
  public mapToPayerTypeCodeControl = new FormControl<boolean>(false);
  public referralRequiredControl = new FormControl<boolean>(false);
  public eligiblePayerControl = new FormControl<boolean>(false);
  public requiresAbnCheckingControl = new FormControl<boolean>(false);
  public englishFormattingTemplateUidControl = new FormControlExtended<string[]>([]);
  public spanishFormattingTemplateUidControl = new FormControlExtended<string[]>([]);

  public isFeeScheduleDisabled = true;
  public isEnglishFormattingTemplateRequired = false;
  public isSpanishFormattingTemplateRequired = false;

  public listItemDefaultValidation: FormGroupValidationRules = {
    ERAPayerID: {
      MaxLength: 8000,
    },
  };

  constructor(
    injector: Injector,
    ) {
    super(injector);

    this.useProviderFeeScheduleControl.valueChanges.pipe(this.takeUntilDestroy()).subscribe((useProviderFeeSchedule: boolean) => {
      this.isFeeScheduleDisabled = useProviderFeeSchedule;
      const validators = this.fcListValidation.getValidators(`FeeScheduleUid`);
      if (!useProviderFeeSchedule) {
        validators.push(Validators.required);
        this.feeScheduleUidControl.showErrorFlag = true;
      }
      this.feeScheduleUidControl.setValidators(validators);
      this.feeScheduleUidControl.updateValueAndValidity();
    });

    // Make Eligibility Payer FindControl required when Eligibility ClearingHouse FindControl is not empty
    this.eligibilityClearingHouseUidControl
      .valueChanges
      .pipe(
        this.takeUntilDestroy()
      ).subscribe((eligibilityClearingHouseUids: string[]) => {
        this.isEligibilityPayerFindControlRequired = eligibilityClearingHouseUids.length > 0;

        const validators = this.fcListValidation.getValidators(`EligibilityPayerUid`);
        if (this.isEligibilityPayerFindControlRequired) {
          validators.push(Validators.required);
          this.eligibilityPayerUidControl.showErrorFlag = true;
        }
        this.eligibilityPayerUidControl.setValidators(validators);
        this.eligibilityPayerUidControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });

    combineLatest([
      this.requiresAbnCheckingControl.valueChanges,
      this.englishFormattingTemplateUidControl.valueChanges,
      this.spanishFormattingTemplateUidControl.valueChanges,
    ])
      .pipe(this.takeUntilDestroy())
      .subscribe(([requiresAbnChecking, englishFormattingTemplateUid, spanishFormattingTemplateUid]) => {
        const isEnglishFormattingTemplateValidators = this.fcListValidation.getValidators(`EnglishFormattingTemplateUid`);
        const isSpanishormattingTemplateValidators = this.fcListValidation.getValidators(`SpanishFormattingTemplateUid`);
        if (requiresAbnChecking && englishFormattingTemplateUid?.length === 0 && spanishFormattingTemplateUid?.length === 0) {
          this.isEnglishFormattingTemplateRequired = true;
          this.isSpanishFormattingTemplateRequired = true;
          isEnglishFormattingTemplateValidators.push(Validators.required);
          isSpanishormattingTemplateValidators.push(Validators.required);
          this.englishFormattingTemplateUidControl.showErrorFlag = true;
          this.spanishFormattingTemplateUidControl.showErrorFlag = true;
        } else {
          this.isEnglishFormattingTemplateRequired = false;
          this.isSpanishFormattingTemplateRequired = false;
        }

        // Set Validators and validate
        this.englishFormattingTemplateUidControl.setValidators(isEnglishFormattingTemplateValidators);
        this.englishFormattingTemplateUidControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.spanishFormattingTemplateUidControl.setValidators(isSpanishormattingTemplateValidators);
        this.spanishFormattingTemplateUidControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

        // Disable controls when RequiresAbnChecking checkbox true
        if (requiresAbnChecking) {
          this.englishFormattingTemplateUidControl.enable({ emitEvent: false });
          this.spanishFormattingTemplateUidControl.enable({ emitEvent: false });
        } else {
          this.englishFormattingTemplateUidControl.disable({ emitEvent: false });
          this.spanishFormattingTemplateUidControl.disable({ emitEvent: false });
        }
      });
  }

  public ngOnInit(): void {
    this.validateCreditType();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemContext != null) {
      this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
    }
    if (changes.form != null) {
      this.addControlsToForm();
    }
    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public getFindControlValidation(controlName: string): any {
    if (this.fcListValidation.getValidators(controlName).length > 0) {
      return this.fcListValidation.getValidators(controlName);
    } else {
      return this.listItemDefaultValidation[controlName]?.IsRequired;
    }
  }

  public validateCreditType(): void {
    const validators = this.fcListValidation.getValidators('AutoWriteoffCreditTypeUid');
    validators.push((control: FormControlExtended<string>): ValidationErrors | null => {
      if (control.additionalData?.Name && !(control.additionalData?.TransactionTypeUid === SystemGuid.TransactionType_Adjustment)) {
        return {
          autoWriteoffAdjType: {
            name: control.additionalData.Name,
          }
        };
      }

      return null;
    });
    this.autoWriteoffCreditTypeUidControl.setValidators(validators);
  }

  public onAutoWriteoffCreditTypeUidDataPopulate(autoWriteoffAdjTypeArray: FcAutoWriteoffAdjTypeDataDTO[]): void {
    const autoWriteoffAdjTypeData: FcAutoWriteoffAdjTypeDataDTO = { ...autoWriteoffAdjTypeArray[0] };
    this.autoWriteoffCreditTypeUidControl.additionalData = autoWriteoffAdjTypeData as unknown as Record<string, string>;
    if (autoWriteoffAdjTypeData.Name) {
      this.validateCreditType();
      this.autoWriteoffCreditTypeUidControl.setValue([autoWriteoffAdjTypeData.CreditTypeUid]);
    } else {
      this.autoWriteoffCreditTypeUidControl.setValue([this.parseFindControlValue(this.listItem.AutoWriteoffCreditTypeUid)]);
    }
  }

  public onCalendarDateChangeIcd(event: Date | null): void {
    if (!event) return;
    this.icdControl.setValue(event);
    this.icdControl.markAsDirty();
  }

  public onCalendarDateChangeEm(event: Date | null): void {
    if (!event) return;
    this.emControl.setValue(event);
    this.emControl.markAsDirty();
  }

  private addControlsToForm(): void {
    this.form.addControl('AccountTypeUid', this.accountTypeUidControl);
    this.form.addControl('UseProviderFeeSchedule', this.useProviderFeeScheduleControl);
    this.form.addControl('FeeScheduleUid', this.feeScheduleUidControl);
    this.form.addControl('AmountAllowedFeeScheduleUid', this.amountAllowedFeeScheduleUidControl);
    this.form.addControl('ReimbursementRuleUid', this.reimbursementRuleUidControl);
    this.form.addControl('ERAPayerID', this.eraPayerIdControl);
    this.form.addControl('PrmRuleGroupUid', this.eraRuleGroupUidControl);
    this.form.addControl('AutoWriteoffCreditTypeUid', this.autoWriteoffCreditTypeUidControl);
    this.form.addControl('NoNetwork', this.noNetworkControl);
    this.form.addControl('EligibilityClearingHouseUid', this.eligibilityClearingHouseUidControl);
    this.form.addControl('EligibilityPayerUid', this.eligibilityPayerUidControl);
    this.form.addControl('EligibilityFormatUid', this.eligibilityFormatUidControl);
    this.form.addControl('EnrollmentClearingHouseUid', this.enrollmentClearingHouseUidControl);
    this.form.addControl('DemographicsClearingHouseUid', this.demographicsClearingHouseUidControl);
    this.form.addControl('AlternateInsurancePlanUid', this.alternateInsurancePlanUidControl);
    this.form.addControl('ICD10Date', this.icdControl);
    this.form.addControl('EM2021Date', this.emControl);
    this.form.addControl('ReferralRequired', this.eligiblePayerControl);
    this.form.addControl('EligibleCCM_CPO', this.mapToPayerTypeCodeControl);
    this.form.addControl('MapToPayerTypeCode', this.referralRequiredControl);
    this.form.addControl('RequiresABNChecking', this.requiresAbnCheckingControl);
    this.form.addControl('EnglishFormattingTemplateUid', this.englishFormattingTemplateUidControl);
    this.form.addControl('SpanishFormattingTemplateUid', this.spanishFormattingTemplateUidControl);
  }

  private setForm(): void {
    // Account Type
    this.accountTypePks = this.getFindControlValue(this.listItem?.AccountTypeUid);

    // Use provider's Fee List
    this.useProviderFeeScheduleControl.setValue(this.listItem?.UseProviderFeeSchedule ?? true);

    // Fee Schedule
    this.feeSchedulePks = this.getFindControlValue(this.listItem?.FeeScheduleUid);

    // ERA Payer ID
    this.eraPayerIdControl.setValidators(this.fcListValidation.getValidators('ERAPayerID'));
    this.eraPayerIdControl.setValue(this.listItem?.ERAPayerID ?? '');

    // No Network
    this.noNetworkControl.setValue(this.listItem?.NoNetwork ?? false);

    const allowedFeeScheduleDefualt =
    this.itemContext.mode === FcItemModalMode.New ? [SystemGuid.AmountAllowedListDefaultOptionUid] : [];
    this.allowedFeeSchedulePks = this.getFindControlValue(this.listItem?.AmountAllowedFeeScheduleUid ?? allowedFeeScheduleDefualt);
    this.reimbursementRulePks = this.getFindControlValue(this.listItem?.ReimbursementRuleUid);
    this.eraRuleGroupPks = this.getFindControlValue(this.listItem?.PrmRuleGroupUid);
    this.autoWriteoffCreditTypePks = this.getFindControlValue(this.listItem?.AutoWriteoffCreditTypeUid);
    this.eligibilityClearingHousePks = this.getFindControlValue(this.listItem?.EligibilityClearingHouseUid);
    this.eligibilityPayerPks = this.getFindControlValue(this.listItem?.EligibilityPayerUid);
    this.eligibilityFormatPks = this.getFindControlValue(this.listItem?.Eligibility_ListClaimFormatUid);
    this.enrollmentClearingHousePks = this.getFindControlValue(this.listItem?.EnrollmentClearingHouseUid);
    this.demographicsClearingHousePks = this.getFindControlValue(this.listItem?.DemographicsClearingHouseUid);
    this.alternateInsurancePlanPks = this.getFindControlValue(this.listItem?.AlternateInsurancePlanUid);
    this.englishFormattingTemplatePks = this.getFindControlValue(this.listItem?.EnglishFormattingTemplateUid);
    this.spanishFormattingTemplatePks = this.getFindControlValue(this.listItem?.SpanishFormattingTemplateUid);

    // ICD Control
    this.icdControl.setValidators(this.fcListValidation.getValidators('ICD10Date'));
    this.setDateToControl(this.icdControl, this.listItem?.ICD10Date ?? new Date());

    // EM Control
    this.emControl.setValidators(this.fcListValidation.getValidators('EM2021Date'));
    this.setDateToControl(this.emControl, this.listItem?.EM2021Date ?? new Date());

    this.referralRequiredControl.setValue(this.listItem?.ReferralRequired ?? false);
    this.eligiblePayerControl.setValue(this.listItem?.EligibleCCM_CPO ?? false);
    this.mapToPayerTypeCodeControl.setValue(this.listItem?.MapToPayerTypeCode ?? false);
    this.requiresAbnCheckingControl.setValue(this.listItem?.RequiresABNChecking ?? false);
  }

  private setDateToControl(control: FormControl, date: Date | string | number) {
    date = date instanceof Date ? date : new Date(date);
    if (date.toString() !== 'Invalid Date') {
      control.setValue(date);
    } else {
      control.setValue(new Date());
    }
  }
}
